import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { SeasonService } from 'src/app/services/season.service';
import { SeasonPassCategoryService } from 'src/app/services/season-pass-category.service';
import { PlacesService } from 'src/app/services/places.service';
import { SeatMapService } from 'src/app/services/seat-map.service';
import { InfoCardComponent } from '../../../shared/info-card/info-card.component';
import { PanelTitleService } from 'src/app/services/panel-title.service';
import { SeasonProductAssignment } from 'src/app/interfaces/season';

/** Fila de la tabla de niveles: `id` null es la fila "todos los niveles". */
interface LevelRow {
  id: number | null;
  name: string;
}

@Component({
  selector: 'app-season-form',
  templateUrl: './season-form.component.html',
  styleUrls: ['./season-form.component.scss']
})
export class SeasonFormComponent implements OnInit, OnDestroy {

  form: FormGroup;
  isEdit: boolean = false;
  seasonId: number | null = null;
  loaded: boolean = false;
  saving: boolean = false;

  business: any;

  // Matriz de la temporada: qué producto se vende a qué categoría de abonado en
  // qué nivel de butaca. El precio no se toca aquí — cada combinación
  // categoría × nivel × variante (con/sin carnet) es un producto propio con su
  // precio, creado desde la pestaña Productos.
  products: any[] = [];
  categories: any[] = [];
  assignments: SeasonProductAssignment[] = [];

  // El nivel pertenece al espacio, y la temporada no tiene espacio propio, así
  // que se elige aquí de qué recinto se configuran los niveles. Con un solo
  // espacio (el caso normal de un club) se selecciona solo.
  spaces: any[] = [];
  selectedSpaceId: number | null = null;
  levelRows: LevelRow[] = [{ id: null, name: 'Todos los niveles' }];
  levelsLoading: boolean = false;

  private sub: Subscription;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private seasonService: SeasonService,
    private categoryService: SeasonPassCategoryService,
    private placesService: PlacesService,
    private seatMapService: SeatMapService,
    private dialog: MatDialog,
    private panelTitleService: PanelTitleService,
  ) { }

  ngOnInit(): void {
    this.business = JSON.parse(localStorage.getItem('business') || '{}');

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(255)]],
      description: [''],
      start_date: ['', Validators.required],
      end_date: ['', Validators.required],
      active: [true],
    }, { validators: this.dateRangeValidator });

    this.sub = this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEdit = true;
        this.seasonId = +params['id'];
        this.loadSeason(this.seasonId);
        this.loadAssignments(this.seasonId);
      } else {
        this.loaded = true;
      }
      this.loadCategories();
      this.loadSpaces();
    });
  }

  ngOnDestroy(): void {
    this.panelTitleService.setSubtitle(null);
    if (this.sub) this.sub.unsubscribe();
  }

  loadSeason(id: number) {
    this.seasonService.getSeasonById(this.business.id, id).subscribe({
      next: (res) => {
        const s = res.data;
        this.panelTitleService.setSubtitle(s.name);
        this.form.patchValue({
          name: s.name,
          description: s.description || '',
          start_date: s.start_date ? s.start_date.substring(0, 10) : '',
          end_date: s.end_date ? s.end_date.substring(0, 10) : '',
          active: s.active,
        });
        this.loaded = true;
      },
      error: () => {
        this.openInfoCard('Error al cargar la temporada');
        this.router.navigate(['/panel-control/sports-passes/seasons']);
      }
    });
  }

  private loadCategories() {
    this.categoryService.getCategories(true).subscribe({
      next: (res) => { this.categories = res.data || []; },
      error: () => { this.categories = []; }
    });
  }

  private loadAssignments(seasonId: number) {
    this.seasonService.getProductAssignments(this.business.id, seasonId).subscribe({
      next: (res) => {
        this.products = res?.data?.products || [];
        this.assignments = (res?.data?.assignments || []).map((a: any) => ({
          id_product: +a.id_product,
          id_category: a.id_category != null ? +a.id_category : null,
          id_level: a.id_level != null ? +a.id_level : null,
        }));
      },
      error: () => {
        this.products = [];
        this.assignments = [];
      }
    });
  }

  private loadSpaces() {
    // `all=true` trae todos los espacios de la compañía sin paginar.
    this.placesService.getListFilterPlaces(this.business.id, 1, null, null, true).subscribe({
      next: (res) => {
        this.spaces = res?.data?.data || res?.data || [];
        if (this.spaces.length === 1) {
          this.onSpaceChange(this.spaces[0].id);
        }
      },
      error: () => { this.spaces = []; }
    });
  }

  onSpaceChange(spaceId: number | null) {
    this.selectedSpaceId = spaceId ? +spaceId : null;
    this.levelRows = [{ id: null, name: 'Todos los niveles' }];

    if (!this.selectedSpaceId) return;

    this.levelsLoading = true;
    this.placesService.getPlaceById(this.selectedSpaceId).subscribe({
      next: (res: any) => {
        const version = res?.data?.current_version;

        if (!version) {
          // Espacio sin mapa: solo cabe la asignación genérica.
          this.levelsLoading = false;
          return;
        }

        this.seatMapService.getLevelsVersionMap(this.selectedSpaceId as number, version).subscribe({
          next: (levelsRes: any) => {
            const levels = (levelsRes?.data || []).slice().sort((a: any, b: any) => {
              const oa = a?.orderBuy ?? a?.order_buy ?? Infinity;
              const ob = b?.orderBuy ?? b?.order_buy ?? Infinity;
              return oa - ob;
            });
            this.levelRows = [
              { id: null, name: 'Todos los niveles' },
              ...levels.map((l: any) => ({ id: +l.id, name: l.name })),
            ];
            this.levelsLoading = false;
          },
          error: () => { this.levelsLoading = false; }
        });
      },
      error: () => { this.levelsLoading = false; }
    });
  }

  /** Productos ya asignados a esa celda (categoría × nivel). */
  productsIn(categoryId: number | null, levelId: number | null): any[] {
    return this.assignments
      .filter(a => a.id_category === categoryId && a.id_level === levelId)
      .map(a => this.products.find(p => +p.id === a.id_product))
      .filter(p => !!p);
  }

  /** Los que se pueden añadir a esa celda: todos los de la compañía menos los que ya están. */
  productsAvailableIn(categoryId: number | null, levelId: number | null): any[] {
    const assigned = this.assignments
      .filter(a => a.id_category === categoryId && a.id_level === levelId)
      .map(a => a.id_product);

    return this.products.filter(p => !assigned.includes(+p.id));
  }

  addAssignment(categoryId: number | null, levelId: number | null, productId: any) {
    const id = +productId;
    if (!id) return;

    const exists = this.assignments.some(
      a => a.id_product === id && a.id_category === categoryId && a.id_level === levelId
    );

    if (!exists) {
      this.assignments = [...this.assignments, { id_product: id, id_category: categoryId, id_level: levelId }];
    }
  }

  removeAssignment(categoryId: number | null, levelId: number | null, productId: number) {
    this.assignments = this.assignments.filter(
      a => !(a.id_product === +productId && a.id_category === categoryId && a.id_level === levelId)
    );
  }

  /**
   * Asignaciones que no se ven en la tabla porque son de niveles de otro
   * espacio (o de un nivel que ya no está en la versión vigente del mapa). Se
   * mantienen tal cual al guardar: el PUT reemplaza la matriz completa, así que
   * si no se enviasen se borrarían sin que nadie las haya tocado.
   */
  private get hiddenAssignments(): SeasonProductAssignment[] {
    const visibleLevelIds = this.levelRows.map(l => l.id);
    const visibleCategoryIds: (number | null)[] = this.categories.map(c => +c.id);

    return this.assignments.filter(
      a => !visibleLevelIds.includes(a.id_level) || !visibleCategoryIds.includes(a.id_category)
    );
  }

  get hiddenAssignmentsCount(): number {
    return this.hiddenAssignments.length;
  }

  save() {
    if (this.form.invalid || this.saving) return;
    this.saving = true;

    const formValue = { ...this.form.value };

    if (this.isEdit && this.seasonId) {
      this.seasonService.updateSeason(this.business.id, this.seasonId, formValue).subscribe({
        next: () => {
          this.saveAssignments(this.seasonId as number, 'Temporada actualizada correctamente');
        },
        error: (err) => {
          this.saving = false;
          this.openInfoCard(this.extractErrorMessage(err, 'Error al actualizar la temporada'));
        }
      });
    } else {
      this.seasonService.createSeason(this.business.id, formValue).subscribe({
        next: (res) => {
          this.saveAssignments(res?.data?.id, 'Temporada creada correctamente');
        },
        error: (err) => {
          this.saving = false;
          this.openInfoCard(this.extractErrorMessage(err, 'Error al crear la temporada'));
        }
      });
    }
  }

  /**
   * La matriz se guarda completa en una sola llamada (el PUT reemplaza lo que
   * haya), así que se envían también las asignaciones que la tabla no muestra.
   */
  private saveAssignments(seasonId: number, successMessage: string) {
    if (!seasonId) {
      this.finishSave(successMessage);
      return;
    }

    this.seasonService.updateProductAssignments(this.business.id, seasonId, this.assignments).subscribe({
      next: () => this.finishSave(successMessage),
      error: (err) => {
        this.saving = false;
        this.openInfoCard(this.extractErrorMessage(err, 'La temporada se guardó, pero no se pudieron guardar las asignaciones de productos.'));
        this.router.navigate(['/panel-control/sports-passes/seasons']);
      }
    });
  }

  private finishSave(message: string) {
    this.saving = false;
    this.openInfoCard(message);
    this.router.navigate(['/panel-control/sports-passes/seasons']);
  }

  cancel() {
    this.router.navigate(['/panel-control/sports-passes/seasons']);
  }

  private dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const start = control.get('start_date')?.value;
    const end = control.get('end_date')?.value;
    if (start && end && end < start) {
      return { dateRange: true };
    }
    return null;
  }

  private extractErrorMessage(err: any, fallback: string): string {
    if (err?.status === 422 && err?.error?.errors && typeof err.error.errors === 'object' && !Array.isArray(err.error.errors)) {
      const first: any = Object.values(err.error.errors)[0];
      if (Array.isArray(first) && first.length) {
        return first[0];
      }
    }
    return err?.error?.message || fallback;
  }

  private openInfoCard(msg: string) {
    this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: { text: msg }
    });
  }
}
